import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Landmark, Play, Pause, Volume2, X, Youtube } from "lucide-react";
import { Header } from "@/components/Header";
import { useLang } from "@/context/LangContext";
import { extractYouTubeId } from "@/lib/youtube";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_BADGE = { en: "EN", si: "සිං", ta: "தமிழ்" };

const StoryCard = ({ item }) => {
  const { lang } = useLang();
  const [expanded, setExpanded] = useState(false);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [videoOpen, setVideoOpen] = useState(null);
  const al = item.audio_langs || {};
  const playLang = [lang, "si", "ta", "en"].find((l) => al[l]) || (item.audio_url ? lang : null);
  const audioSrc = (playLang && al[playLang]) || item.audio_url || "";
  useEffect(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(false);
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, [audioSrc]);
  const clips = [
    ...(item.video_url ? [{ label: "Watch", url: item.video_url }] : []),
    ...((item.videos || []).map((v) => ({ label: v.label || "Watch", url: v.url }))),
  ].map((v) => ({ ...v, id: extractYouTubeId(v.url) })).filter((v) => v.id);

  const toggleAudio = () => {
    if (!audioSrc) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(audioSrc);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const long = (item.story || "").length > 220;
  return (
    <div data-testid={`heritage-card-${item.id}`} className="rounded-3xl overflow-hidden break-inside-avoid" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
      {item.image_url && (
        <div className="relative">
          <img src={item.image_url} alt={item.title} className="w-full h-52 object-cover" loading="lazy" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(20,14,8,0.75) 100%)" }} />
          <div className="absolute bottom-3 left-4 right-4">
            <h3 className="font-en text-xl font-extrabold text-white leading-tight">{item.title}</h3>
            {item.subtitle && <p className="font-ui text-xs text-white/80 mt-0.5">{item.subtitle}</p>}
          </div>
        </div>
      )}
      <div className="p-5">
        {!item.image_url && <h3 className="font-en text-xl font-extrabold" style={{ color: "var(--text)" }}>{item.title}</h3>}
        <p className={`font-ui text-sm leading-relaxed whitespace-pre-line ${!expanded && long ? "line-clamp-4" : ""}`} style={{ color: "var(--text-2)" }}>{item.story}</p>
        {Array.isArray(item.gallery) && item.gallery.length > 0 && (
          <div className="grid grid-cols-3 gap-1.5 mt-3" data-testid={`heritage-gallery-${item.id}`}>
            {item.gallery.map((g, i) => (
              <button key={i} onClick={() => setLightbox(g)} data-testid={`heritage-gallery-${item.id}-${i}`} className="block overflow-hidden rounded-lg" style={{ border: "1px solid var(--border)" }}>
                <img src={g} alt={`${item.title} ${i + 1}`} loading="lazy" className="w-full h-16 object-cover transition-transform duration-300 hover:scale-110" />
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 mt-3">
          {long && (
            <button onClick={() => setExpanded((e) => !e)} data-testid={`heritage-more-${item.id}`} className="font-ui text-xs font-bold" style={{ color: "var(--accent)" }}>
              {expanded ? "Show less" : "Read more"}
            </button>
          )}
          {audioSrc ? (
            <button onClick={toggleAudio} data-testid={`heritage-audio-${item.id}`} className="inline-flex items-center gap-1.5 font-ui text-xs font-bold" style={{ color: "var(--accent)" }}>
              {playing ? <Pause size={14} /> : <Play size={14} />} Listen
              <span data-testid={`heritage-audio-lang-${item.id}`} className="inline-flex items-center rounded-full px-1.5 py-[1.5px] text-[10px] font-extrabold leading-none tracking-wide" style={{ background: "linear-gradient(135deg, #f4b41a 0%, #d97706 55%, #8c1c13 100%)", color: "#fff5e1", boxShadow: "0 1px 2px rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.25)" }}>
                {LANG_BADGE[playLang] || "EN"}
              </span>
            </button>
          ) : (
            <span data-testid={`heritage-audio-soon-${item.id}`} className="inline-flex items-center gap-1.5 font-ui text-xs" style={{ color: "var(--text-muted)" }}>
              <Volume2 size={14} /> Narration coming soon
            </span>
          )}
          {clips.map((v, i) => (
            <button key={i} onClick={() => setVideoOpen(v.id)} data-testid={`heritage-video-${item.id}-${i}`} className="inline-flex items-center gap-1.5 font-ui text-xs font-bold" style={{ color: "#dc2626" }}>
              <Youtube size={15} /> {v.label}
            </button>
          ))}
        </div>
      </div>
      {lightbox && (
        <div onClick={() => setLightbox(null)} data-testid={`heritage-lightbox-${item.id}`} className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(10,7,4,0.88)" }}>
          <img src={lightbox} alt={item.title} className="max-w-full max-h-[85vh] rounded-xl" />
          <button onClick={() => setLightbox(null)} aria-label="Close" className="absolute top-4 right-4 grid place-items-center w-11 h-11 rounded-full" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}><X size={22} /></button>
        </div>
      )}
      {videoOpen && (
        <div onClick={() => setVideoOpen(null)} data-testid={`heritage-video-modal-${item.id}`} className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(10,7,4,0.9)" }}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl">
            <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingBottom: "56.25%", background: "#000" }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${videoOpen}?rel=0&autoplay=1`}
                title={item.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <a href={`https://www.youtube.com/watch?v=${videoOpen}`} target="_blank" rel="noopener noreferrer" data-testid={`heritage-video-open-${item.id}`} className="inline-flex items-center gap-1.5 font-ui text-xs font-bold mt-3 text-white/90">
              <Youtube size={15} /> Open on YouTube
            </a>
          </div>
          <button onClick={() => setVideoOpen(null)} aria-label="Close" className="absolute top-4 right-4 grid place-items-center w-11 h-11 rounded-full" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}><X size={22} /></button>
        </div>
      )}
    </div>
  );
};

export default function Heritage() {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/heritage`).then((r) => setItems(r.data || [])).catch(() => {}); }, []);

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/rooms/sindu-kamare" data-testid="heritage-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft size={16} /> Back
        </Link>
        <div className="text-center mb-8">
          <span className="grid place-items-center w-16 h-16 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Landmark size={30} /></span>
          <h1 className="font-en text-4xl sm:text-5xl font-extrabold mt-4" style={{ color: "var(--text)" }}>Living Heritage of Sri Lanka</h1>
          <p className="font-ui text-base mt-3 max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--text-2)" }}>
            The unsung souls who carry our island's spirit — fishermen on stilts, tea pluckers in the mist, toddy tappers among the palms, forest monks in silent caves, and the artists who keep our stories alive.
          </p>
        </div>
        {items.length === 0 ? (
          <p className="text-center font-ui text-sm py-10" style={{ color: "var(--text-muted)" }} data-testid="heritage-empty">Stories coming soon. 🌾</p>
        ) : (
          <div className="columns-1 sm:columns-2 gap-4 space-y-4" data-testid="heritage-wall">
            {items.map((it) => <StoryCard key={it.id} item={it} />)}
          </div>
        )}
      </div>
    </div>
  );
}
